// Attacker + Target — bordered field rows with copy actions.



import React from "react";

import PropTypes from "prop-types";

import { Box, Chip, Divider, Stack, Typography } from "@mui/material";

import { Swords, Server } from "lucide-react";



import colors from "../../../../../utils/colors";

import {

  BORDER_COLOR,

  PRIMARY_COLOR,

  TEXT_SECONDARY,

} from "../wafEventDrawerShared";

import { spSectionLabelSx, NetworkFieldRow, spIconBoxForColor, spNetworkFieldLabelSx, spNetworkFieldRowSx, spNetworkFieldValueSx, SP } from "./smartParseDrawerUi";

import AbuseConfidenceScoreCard from "./AbuseConfidenceScoreCard";

import RequestHeadersSection from "./RequestHeadersSection";



const dash = (value) => (value != null && value !== "" ? String(value) : null);



function ColumnHeader({ label, icon: Icon, iconColor }) {

  return (

    <Stack direction="row" alignItems="center" spacing={0} sx={{ gap: "8px", mb: 0.5 }}>

      <Box sx={spIconBoxForColor(iconColor, "grid")}>

        <Icon size={14} color={iconColor} strokeWidth={2.5} />

      </Box>

      <Typography sx={{ ...spSectionLabelSx, mb: 0 }}>{label}</Typography>

    </Stack>

  );

}



function AttackerSection({ attacker, onCopy }) {

  if (!attacker) return null;



  const geo = attacker.geo || {};

  const ja3 = attacker.fingerprints?.ja3;

  const ja4 = attacker.fingerprints?.ja4;



  return (

    <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>

      <ColumnHeader

        label="ATTACKER"

        icon={Swords}

        iconColor={colors.red[100]}

      />

      <NetworkFieldRow
        label="Source IP"
        value={dash(attacker.source_ip)}
        copyable={!!attacker.source_ip}
        onCopy={onCopy}
      />

      {attacker.source_port != null && (

        <NetworkFieldRow label="Source port" value={dash(attacker.source_port)} />

      )}

      <NetworkFieldRow label="Country" value={dash(geo.country_iso)} />

      {geo.city && <NetworkFieldRow label="City" value={geo.city} />}

      {geo.region && <NetworkFieldRow label="Region" value={geo.region} />}

      <NetworkFieldRow

        label="JA3"

        value={dash(ja3)}

        copyable={!!ja3}

        onCopy={onCopy}

        monospace

      />

      {ja4 && (

        <NetworkFieldRow

          label="JA4"

          value={ja4}

          copyable

          onCopy={onCopy}

          monospace

        />

      )}

    </Stack>

  );

}



function TargetSection({ target, onCopy }) {

  if (!target) return null;



  const status = target.response_status;

  const statusNum = status != null ? Number(status) : null;

  const isErrorStatus = statusNum != null && statusNum >= 400;



  return (

    <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>

      <ColumnHeader

        label="TARGET"

        icon={Server}

        iconColor={PRIMARY_COLOR}

      />

      <NetworkFieldRow

        label="Host"

        value={dash(target.host)}

        copyable={!!target.host}

        onCopy={onCopy}

        valueColor={PRIMARY_COLOR}

      />

      <NetworkFieldRow

        label="URI"

        value={dash(target.uri || target.path)}

        copyable={!!(target.uri || target.path)}

        onCopy={onCopy}

        monospace

      />

      <NetworkFieldRow label="Method" value={dash(target.method)} />

      {target.http_version && (

        <NetworkFieldRow label="HTTP version" value={target.http_version} />

      )}

      {target.alpn && <NetworkFieldRow label="ALPN" value={target.alpn} />}

      <Box sx={spNetworkFieldRowSx}>
        <Typography sx={spNetworkFieldLabelSx}>
          Response status
        </Typography>
        {status != null ? (
          <Chip
            size="small"
            label={String(status)}
            sx={{
              height: 24,
              width: "fit-content",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: "6px",
              backgroundColor: isErrorStatus ? colors.red[10] : colors.green[10],
              color: isErrorStatus ? colors.red[200] : colors.green[300],
              border: `1px solid ${isErrorStatus ? "rgba(251,55,72,0.30)" : "rgba(31,193,107,0.32)"}`,
              "& .MuiChip-label": { px: "10px", py: 0, lineHeight: 1.2 },
            }}
          />
        ) : (
          <Typography sx={spNetworkFieldValueSx}>—</Typography>
        )}
      </Box>

    </Stack>

  );

}



export default function NetworkTargetPanel({ attacker, target, onCopy }) {

  const userAgent = target?.headers?.user_agent;

  const reputation = attacker?.reputation;

  const showFullWidthBlock = reputation != null || userAgent;



  return (

    <Stack spacing={3}>

      <Stack

        direction={{ xs: "column", md: "row" }}

        spacing={0}

        alignItems="flex-start"

        sx={{ gap: `${SP.gridCellX}px` }}

      >

        <AttackerSection attacker={attacker} onCopy={onCopy} />

        <Box sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>

          <Divider sx={{ borderColor: BORDER_COLOR }} />

        </Box>

        <TargetSection target={target} onCopy={onCopy} />

      </Stack>



      {showFullWidthBlock && (

        <Stack spacing={2.5}>

          <AbuseConfidenceScoreCard
            reputation={reputation}
            countryFallback={attacker?.geo?.country_iso}
          />

          <RequestHeadersSection userAgent={userAgent} onCopy={onCopy} />

        </Stack>

      )}

    </Stack>

  );

}



NetworkTargetPanel.propTypes = {

  attacker: PropTypes.object,

  target: PropTypes.object,

  onCopy: PropTypes.func,

};


